import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { videoUrl } from "../../lib/api";

const HERO_VIDEO = videoUrl("hero-fries-burger.mp4");
const HERO_POSTER = videoUrl("hero-poster.jpg");

/**
 * HeroV2 — cinematic hero focused on copy + CTAs over the background video.
 * The central packshot / cream disc / 3D mouse-tilt have been removed because
 * the MP4 packshot had no alpha and produced a heavy "image inside image" look.
 */
export const HeroV2 = () => {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // `canplay` fires sooner than `loadeddata` on iOS Safari and is the earliest
    // moment we can safely fade the video in without a black flicker.
    if (v.readyState >= 2) setReady(true);
    const onReady = () => setReady(true);
    const onError = () => { setFailed(true); setReady(false); };
    v.addEventListener("canplay", onReady);
    v.addEventListener("loadeddata", onReady);
    v.addEventListener("error", onError);

    // iOS Safari occasionally refuses autoplay even with muted+playsInline (low
    // power mode, content-restricted profiles). Try immediately, then retry on
    // the first user gesture so the video catches up without a black flash —
    // the poster underneath stays visible the whole time.
    const tryPlay = () => {
      if (v.paused) v.play().catch(() => {});
    };
    tryPlay();

    const retry = () => tryPlay();
    const opts = { once: true, passive: true };
    window.addEventListener("touchstart", retry, opts);
    window.addEventListener("scroll", retry, opts);
    window.addEventListener("click", retry, { once: true });

    return () => {
      v.removeEventListener("canplay", onReady);
      v.removeEventListener("loadeddata", onReady);
      v.removeEventListener("error", onError);
      window.removeEventListener("touchstart", retry);
      window.removeEventListener("scroll", retry);
      window.removeEventListener("click", retry);
    };
  }, []);

  return (
    <section
      data-testid="hero-v2"
      className="relative min-h-[100svh] w-full overflow-hidden bg-fd-bg"
    >
      {/* Background — poster paints instantly (preloaded in index.html), video crossfades in over it.
          Poster XOR video: exactly one of the two carries the visual at any moment, so they never
          compound into a double-exposed background. */}
      <div className="absolute inset-0">
        {/* Poster layer: visible at first paint, fades out as the video takes over. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${HERO_POSTER})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: ready && !failed ? 0 : 0.42,
            filter: "blur(2px) saturate(1.1)",
            transition: "opacity 0.6s ease-out",
          }}
        />
        <video
          ref={videoRef}
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: ready && !failed ? 0.42 : 0,
            filter: "blur(2px) saturate(1.1)",
            transition: "opacity 0.6s ease-out",
          }}
        />
        {/* Vertical readability gradient — darker top + bottom, lets the video breathe in the middle */}
        <div className="absolute inset-0 bg-gradient-to-b from-fd-bg/65 via-fd-bg/20 to-fd-bg/85"></div>
        {/* Soft gold glow on the left to anchor the headline side */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_25%_55%,rgba(212,162,76,0.12),transparent_65%)]"></div>
      </div>

      {/* Foreground content — headline + CTAs, no packshot, no cream disc */}
      <div className="relative z-10 flex flex-col justify-end min-h-[100svh] px-6 md:px-10 lg:px-14 pt-24 lg:pt-32 pb-16 md:pb-20 lg:pb-24 max-w-[1400px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mono-label-gold mb-5 lg:mb-6 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-fd-gold"></span>
          <span>Frites Dorées · Grossiste B2B · Depuis 2009</span>
        </motion.div>

        {/* Single <h1> for SEO/a11y — two visual lines via <span>, second line in gold gradient.
            Rendered as plain HTML so it is visible immediately on mount. Any opacity-driven
            animation here historically caused a perceptible flash and a delay before the brand
            message could be read. Keep it static. */}
        <h1 className="display-xl text-[3.2rem] sm:text-7xl lg:text-[6.2rem] xl:text-[7.8rem] 2xl:text-[9rem]">
          <span className="block text-fd-text">Frites dorées.</span>
          <span className="block text-gradient-gold">Marges dorées.</span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-6 lg:mt-8 font-body text-base md:text-lg text-fd-text/85 max-w-xl"
        >
          Grossiste moderne pour la restauration rapide : frites surgelées, viandes, sauces, emballages pro. Sourcing sélectionné, accompagnement commercial, solutions multi-sites. 300+ références · 13 gammes · 1 livraison.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-8 lg:mt-10 flex flex-col sm:flex-row gap-3"
        >
          <Link to="/catalogue" data-cursor-hover className="btn-pill btn-primary-v2" data-testid="hero-v2-cta-primary">
            Voir les 13 gammes <ArrowUpRight size={16} />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-v2-cta-secondary"
            className="inline-flex items-center justify-center gap-2 px-7 py-[0.95rem] rounded-full border border-white/35 text-fd-text font-display font-medium uppercase tracking-[0.02em] text-[0.85rem] whitespace-nowrap hover:border-white transition-colors duration-300"
          >
            Parler à un commercial
          </Link>
        </motion.div>

        {/* Tertiary lead path — sober link under the two main CTAs.
            Surfaces "Voir le catalogue" right in the hero without
            cluttering the primary action pair. */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-4 lg:mt-5"
        >
          <Link
            to="/catalogue-feuilletable"
            data-cursor-hover
            data-testid="hero-v2-cta-catalog"
            className="mono-label-gold inline-flex items-center gap-2 hover:text-fd-text transition-colors"
          >
            <ArrowUpRight size={12} /> Feuilletez notre catalogue grossiste
          </Link>
        </motion.div>

        {/* Mobile-only direct phone link — desktop already exposes the number
            in the navbar. On mobile the navbar phone is hidden behind the
            burger menu, so an owner-operator who just wants to call has 2+
            taps. Inline tel: link removes that friction. */}
        <motion.a
          href="tel:+33148680303"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.5 }}
          className="mono-label-gold mt-5 inline-flex items-center gap-2 lg:hidden hover:text-fd-text transition-colors"
          data-testid="hero-v2-cta-tel"
        >
          <Phone size={12} /> 01 48 68 03 03 · Appel direct
        </motion.a>
      </div>
    </section>
  );
};
